import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import { Listing } from '../../types/marketplace';
import { MARKETPLACE_EMOJI, MARKETPLACE_GRADIENT } from '../../constants/marketplace';
import { apiGet } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useUserLocation, calcDistance, formatDistance } from '../../hooks/useUserLocation';
import { slugify, extractId } from '../../utils/slug';

export default function AppMarketplaceDetail() {
  const { id: slugParam } = useParams<{ id: string }>();
  const id = slugParam ? extractId(slugParam) : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const community = (location.state as { community?: string } | null)?.community || searchParams.get('community') || undefined;
  const { authUser, loading: authLoading } = useAuth();
  const userCoords = useUserLocation();

  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id || authLoading) return;
    const params: Record<string, string> = { id };
    if (!authUser && community) params.community = community;
    const endpoint = authUser ? '/api_listing_details' : '/api_listing_details-public';
    apiGet<{ data: Listing }>(endpoint, params, !!authUser)
      .then((res) => setListing(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load listing'))
      .finally(() => setLoading(false));
  }, [id, community, authUser, authLoading]);

  const handleShare = () => {
    const link = listing
      ? `https://sahayy.com/marketplace/${slugify(listing.title)}-${listing.id}?community=${encodeURIComponent(listing.community || '')}`
      : window.location.href;
    const emoji = listing ? (MARKETPLACE_EMOJI[listing.category] || '🛒') : '';
    const priceStr = listing ? (listing.listingType === 'Free' ? 'Free' : listing.price != null ? `${listing.currency || '€'}${listing.price}` : '') : '';
    const message = listing
      ? `🛒 ${listing.title}\n${emoji} ${listing.category}${priceStr ? `\n💰 ${priceStr}` : ''}${listing.location ? `\n📍 ${listing.location}` : ''}${listing.community ? `\n🏘️ ${listing.community}` : ''}\n\nView on Sahayy 👇\n${link}`
      : link;
    navigator.clipboard.writeText(message).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error || !listing) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />
      <div style={{ maxWidth: '480px', margin: '6rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
        <p style={{ fontSize: '2.5rem', margin: '0 0 1rem' }}>🛒</p>
        <h2 style={{ color: '#111', fontWeight: 700, margin: '0 0 0.5rem' }}>Listing not found</h2>
        <p style={{ color: '#6b7280', margin: '0 0 2rem' }}>{error || 'This listing does not exist.'}</p>
        <button onClick={() => navigate('/marketplace')} style={{ padding: '0.75rem 2rem', backgroundColor: '#111', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>← Back to Marketplace</button>
      </div>
    </div>
  );

  const isCreator = authUser?.userId === listing.user_id;
  const emoji = MARKETPLACE_EMOJI[listing.category] || '🛒';
  const gradient = MARKETPLACE_GRADIENT[listing.category] || MARKETPLACE_GRADIENT['Other'];
  const images = listing.images?.filter(Boolean) || [];
  const hasImage = images.length > 0;
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const deepLink = `sahayy://marketplace/${listing.id}?community=${encodeURIComponent(listing.community || '')}`;
  const priceLabel = listing.listingType === 'Free' ? 'Free' : listing.price != null ? `${listing.currency || '€'}${listing.price}` : 'Contact for price';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />

      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '1.25rem 1.5rem 8rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
          <button onClick={() => navigate('/marketplace')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#111', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}>← Marketplace</button>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {isCreator && (
              <button onClick={() => navigate(`/marketplace/edit?id=${listing.id}&community=${encodeURIComponent(listing.community || '')}`)}
                style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>✏️ Edit</button>
            )}
            <button onClick={handleShare} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: copied ? '#16a34a' : '#111' }}>{copied ? '✓ Copied' : '↑ Share'}</button>
          </div>
        </div>

        {/* Photo grid */}
        <div className="hero-grid" style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '420px', borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {images[0] ? <img src={images[0]} alt={listing.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '6rem' }}>{emoji}</span></div>}
          </div>
          <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
            {[1, 2, 3, 4].map((idx) => (
              <div key={idx} style={{ position: 'relative', overflow: 'hidden' }}>
                {images[idx] ? <img src={images[idx]} alt={`${listing.title} ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #f0f0ee 0%, #e8e6e3 100%)' }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile carousel */}
        <div className="hero-carousel" style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
          {hasImage ? <img key={imgIndex} src={images[imgIndex]} alt={listing.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '5rem' }}>{emoji}</span></div>}
          {images.length > 1 && (
            <>
              <button onClick={() => setImgIndex(i => (i - 1 + images.length) % images.length)} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>‹</button>
              <button onClick={() => setImgIndex(i => (i + 1) % images.length)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>›</button>
              <div style={{ position: 'absolute', bottom: '10px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '5px' }}>
                {images.map((_, i) => <div key={i} onClick={() => setImgIndex(i)} style={{ width: i === imgIndex ? '16px' : '6px', height: '6px', borderRadius: '3px', backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.2s' }} />)}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="detail-columns" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '4rem', alignItems: 'start' }}>
          <div>
            <div style={{ padding: '2rem 0 1.5rem', borderBottom: '1px solid #f0f0f0' }}>
              <h1 style={{ color: '#111', fontWeight: 800, fontSize: 'clamp(1.5rem,3vw,2rem)', margin: '0 0 0.75rem', letterSpacing: '-0.03em', lineHeight: 1.2 }}>{listing.title}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', color: '#6b7280', fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 600, color: '#111' }}>{emoji} {listing.category}</span>
                {listing.listingType && <span style={{ backgroundColor: listing.listingType === 'Free' ? '#dcfce7' : '#f3f4f6', color: listing.listingType === 'Free' ? '#16a34a' : '#374151', padding: '2px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>{listing.listingType}</span>}
                {listing.condition && <span>Condition: {listing.condition}</span>}
                {listing.community && <span style={{ color: '#9ca3af' }}>{listing.community}</span>}
              </div>
            </div>

            {listing.location && (
              <div style={{ padding: '1.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                <h2 style={{ margin: '0 0 0.875rem', fontWeight: 700, fontSize: '1rem', color: '#111' }}>Location</h2>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.location)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#374151', fontSize: '0.95rem', textDecoration: 'none', display: 'block' }}>
                  📍 {listing.location}
                  {userCoords && listing.latitude && listing.longitude && (
                    <span style={{ color: '#10b981', marginLeft: '8px', fontSize: '0.82rem', fontWeight: 600 }}>{formatDistance(calcDistance(userCoords.lat, userCoords.lng, listing.latitude, listing.longitude))}</span>
                  )}
                  {' '}<span style={{ color: '#FF385C', fontSize: '0.82rem', fontWeight: 600 }}>↗</span>
                </a>
              </div>
            )}

            <div style={{ padding: '1.5rem 0' }}>
              <h2 style={{ margin: '0 0 0.875rem', fontWeight: 700, fontSize: '1rem', color: '#111' }}>About this listing</h2>
              <p style={{ color: '#374151', fontSize: '0.975rem', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{listing.description}</p>
            </div>
          </div>

          <div className="detail-sidebar" style={{ position: 'sticky', top: '80px', paddingTop: '2rem' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 6px 30px rgba(0,0,0,0.08)' }}>
              <p style={{ margin: '0 0 0.25rem', fontWeight: 800, fontSize: '1.75rem', color: '#111', letterSpacing: '-0.03em' }}>{priceLabel}</p>
              {listing.condition && <p style={{ margin: '0 0 1.25rem', fontSize: '0.82rem', color: '#9ca3af' }}>{listing.condition}</p>}

              <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', padding: '0.875rem', background: 'linear-gradient(135deg,#FF385C,#e11d48)', color: '#fff', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', marginBottom: '0.75rem', boxShadow: '0 4px 14px rgba(255,56,92,0.35)' }}>Contact Seller →</a>

              <button onClick={handleShare} style={{ width: '100%', padding: '0.75rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', color: '#111', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer' }}>{copied ? '✓ Message copied!' : '↑ Share listing'}</button>

              <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#374151', fontWeight: 600 }}>Get the full experience</p>
                <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ display: 'inline-block', backgroundColor: '#111', color: '#fff', padding: '0.5rem 1.25rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}>Open in App →</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-bottom-bar" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTop: '1px solid #f0f0f0', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', zIndex: 50 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 800, fontSize: '1.25rem', color: '#111', letterSpacing: '-0.02em' }}>{priceLabel}</p>
          {listing.condition && <p style={{ margin: 0, fontSize: '0.78rem', color: '#9ca3af' }}>{listing.condition}</p>}
        </div>
        <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ padding: '0.875rem 1.75rem', background: 'linear-gradient(135deg,#FF385C,#e11d48)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', textDecoration: 'none', boxShadow: '0 4px 14px rgba(255,56,92,0.35)' }}>Contact Seller</a>
      </div>

      <style>{`
        @media (min-width: 768px) { .mobile-bottom-bar { display: none !important; } .hero-carousel { display: none !important; } }
        @media (max-width: 767px) { .hero-grid { display: none !important; } .detail-columns { display: block !important; } .detail-sidebar { display: none !important; } }
      `}</style>
    </div>
  );
}
